import type { Theme } from "@mui/material";
import { formatCurrencyShortText } from "Infrastructure/Utils/CurrencyUtils";
import { generateCompoundInterestData, generateCompoundInterestDataToTarget } from "./CommonChartUtils";

type GenerateSavingsChartDataParams = {
  readonly targetAmount: number;
  readonly monthlyContributionAmount: number;
  readonly durationInMonths: number;
  readonly interestRate: number;
  readonly inflationRate: number;
};

export type SavingsChartData = {
  readonly chartState: ChartState;
  readonly preTargetData?: number[];
  readonly postTargetData?: number[];
  readonly actualDurationInMonths: number;
  readonly milestoneDifferenceRatio: number;
};

export enum ChartState {
  Exceeded = 0,
  FellShort = 1,
  OnTrack = 2,
}

const _getChartState = (finalAmount: number, targetAmount: number) => {
  const tolerance = 0.01;

  if (finalAmount > targetAmount * (1 + tolerance)) {
    return ChartState.Exceeded;
  }

  if (finalAmount < targetAmount * (1 - tolerance)) {
    return ChartState.FellShort;
  }

  return ChartState.OnTrack;
};

const _getAccumulationPhaseData = (
  compoundInterestData: number[],
  chartState: ChartState,
  targetAmount: number,
  monthlyContributionAmount: number,
  finalAmount: number,
  inflationRate: number,
  interestRate: number,
): {
  preTargetData: number[];
  postTargetData: number[];
} => {
  switch (chartState) {
    case ChartState.OnTrack: {
      return {
        preTargetData: compoundInterestData,
        postTargetData: compoundInterestData,
      };
    }

    case ChartState.FellShort: {
      const postTarget = compoundInterestData.concat(
        generateCompoundInterestDataToTarget({
          monthlyContributionAmount,
          startAmount: finalAmount,
          targetAmount,
          inflationRate,
          interestRate,
        }),
      );

      return {
        preTargetData: compoundInterestData,
        postTargetData: postTarget,
      };
    }
    case ChartState.Exceeded: {
      const targetAmountIndex = compoundInterestData.findIndex((amount) => amount > targetAmount);
      const preTarget = compoundInterestData.slice(0, targetAmountIndex);
      const postTarget = preTarget.concat(compoundInterestData.slice(targetAmountIndex));
      return {
        preTargetData: preTarget,
        postTargetData: postTarget,
      };
    }
  }
};

export const generateSavingsChartData = ({
  targetAmount,
  monthlyContributionAmount,
  durationInMonths,
  inflationRate,
  interestRate,
}: GenerateSavingsChartDataParams): SavingsChartData => {
  if (durationInMonths === 1) {
    return {
      milestoneDifferenceRatio: 0,
      preTargetData: [0, targetAmount],
      actualDurationInMonths: 2,
      chartState: ChartState.OnTrack,
    };
  }

  const initialCompoundingData = generateCompoundInterestData({
    monthlyContributionAmount,
    inflationRate,
    interestRate,
    durationInMonths: durationInMonths - 1,
  });

  const finalAmount = initialCompoundingData[initialCompoundingData.length - 1];
  const chartState = _getChartState(finalAmount, targetAmount);
  const { preTargetData, postTargetData } = _getAccumulationPhaseData(
    initialCompoundingData,
    chartState,
    targetAmount,
    monthlyContributionAmount,
    finalAmount,
    inflationRate,
    interestRate,
  );

  const milestoneDifferenceRatio = postTargetData ? Math.abs(preTargetData.length - postTargetData.length) / postTargetData.length : 0;

  const actualDurationInMonths = chartState === ChartState.FellShort ? postTargetData.length : durationInMonths;

  return {
    milestoneDifferenceRatio,
    preTargetData,
    postTargetData,
    chartState,
    actualDurationInMonths,
  };
};

export const getSavingsChartAnnotations = ({
  theme,
  targetAmount,
  data,
}: {
  theme: Theme;
  targetAmount: number;
  data: SavingsChartData;
}) => {
  let fundingX = 0;
  const lastValuePreMilestone = data?.preTargetData?.[data.preTargetData.length - 1];
  const lastValuePostMilestone = data?.postTargetData?.[data.postTargetData.length - 1];

  const commonLineProps = {
    type: "line" as const,
    borderColor: "rgba(0, 0, 0, 0.05)",
    borderWidth: 1,
    borderDash: [8, 4],
  };

  const commonLabelProps = {
    display: true,
    backgroundColor: "transparent",
    color: theme.colors.textSecondary,
    position: "start" as const,
    yAdjust: 10,
    font: {
      weight: 400,
      size: 10,
    },
  };

  switch (data.chartState) {
    case ChartState.OnTrack: {
      fundingX = data.actualDurationInMonths;
      break;
    }

    case ChartState.Exceeded: {
      fundingX = data.preTargetData?.length ?? 1;
      break;
    }

    case ChartState.FellShort: {
      fundingX = data.preTargetData?.length ?? 1;
      break;
    }
  }

  return {
    fundingLine: {
      yMin: 0,
      yMax: targetAmount,
      xMin: fundingX - 1,
      xMax: fundingX - 1,
      borderColor: theme.palette.success.main,
      borderWidth: 1,
      borderDash: [3, 5],
    },
    fundingDot: {
      type: "point" as const,
      xValue: fundingX - 1,
      yValue: 0,
      backgroundColor: theme.palette.success.main,
      borderWidth: 0,
      radius: 4,
    },
    targetValueMilestoneLine: {
      label: {
        ...commonLabelProps,
        content: formatCurrencyShortText(targetAmount),
      },
      ...commonLineProps,
      yMax: targetAmount,
      yMin: targetAmount,
    },
    underfundedMilestoneLine:
      lastValuePreMilestone && data.chartState === ChartState.FellShort && data.milestoneDifferenceRatio > 0
        ? {
            label: {
              ...commonLabelProps,
              content: formatCurrencyShortText(lastValuePreMilestone),
            },
            ...commonLineProps,
            yMax: lastValuePreMilestone,
            yMin: lastValuePreMilestone,
          }
        : undefined,
    overfundedMilestoneLine:
      lastValuePostMilestone && data.chartState === ChartState.Exceeded
        ? {
            label: {
              ...commonLabelProps,
              content: formatCurrencyShortText(lastValuePostMilestone),
            },
            ...commonLineProps,
            yMax: lastValuePostMilestone,
            yMin: lastValuePostMilestone,
          }
        : undefined,
  };
};
